import "@maplibre/maplibre-gl-leaflet";
import L from "leaflet";
import "leaflet.markercluster/dist/leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, toast, ToastContainer } from "react-toastify";
import InventoryIc from "../Components/InventoryIc";
import LocationIc from "../Components/LocationIc";
import useFetchLocations from "../Hooks/useFetchLocations";
// import {
//   getNumberFormatUs,
//   formatNumberFloatFix,
//   formatDecimals,
// } from "../../../utils/formatNumber";
// import { useAccountBalance } from "../../Home/Hooks/useGetAcountBalance";
import "leaflet.vectorgrid";
import { navPaths } from "../../../constants/navbar.constants";
import MissionIc from "../Components/MissionIc";
import "./SeedGo.css";
import { debounce } from "../../../utils/helper";
import useLocationByIP from "../Hooks/useLocationByIP";

const markerOtherIconUrl = "/images/seed-go/marker.png";
const markerParkIconUrl = "/images/seed-go/park.png";
const markerPublicTransportIconUrl = "/images/seed-go/public_transport.png";
const markerSchoolIconUrl = "/images/seed-go/school.png";
const markerSupermarketIconUrl = "/images/seed-go/supermarket.png";

const SeedGo: React.FC = () => {
  const navigate = useNavigate();
  const tele = window.Telegram.WebApp;

  tele.BackButton.show();
  tele.BackButton.onClick(() => navigate("/"));

  const isMobile = ["android", "android_x", "ios"].includes(tele.platform);
  const isAndroid = ["android", "android_x"].includes(tele.platform);

  // const { balance } = useAccountBalance();
  const [map, setMap] = useState<L.Map | null>(null);
  // 40.678520046998045, -73.94754224092637
  const [userLat, setUserLat] = useState<number | null>(0);
  const [userLon, setUserLon] = useState<number | null>(0);
  const markerClusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const existingMarkers = useRef<Set<string>>(new Set());
  const userLocationRef = useRef<L.Marker | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const {
    data: locations,
    loading: loadingLocation,
    refetch: refetchMarker,
  } = useFetchLocations(userLat || 0, userLon || 0, 1); // Mobile

  const { location: desktopLocation, loading: loadingLocationByIP } =
    useLocationByIP(!isMobile);

  const updateDesktopUserLocation = useCallback(
    (latitude: number, longitude: number) => {
      setLoading(true);
      setUserLat(latitude);
      setUserLon(longitude);
      if (map) {
        map.setView([latitude, longitude], 17);
        refetchMarker(latitude, longitude, 1);
        const userLocationMarker = L.divIcon({
          html: `
            <div class="relative flex justify-center items-center">
              <div class="relative z-0 inline-flex h-4 w-4 rounded-full bg-[#4593ee] border-2 border-white"></div>
              <div class="absolute inline-flex h-4 w-4 rounded-full bg-[#4593ee50] user-location animate-ping"></div>
              <div class="absolute inline-flex h-6 w-6 rounded-full bg-[#4593ee40] user-location animate-ping animate-delay-100"></div>
            </div>
          `,
          className: "",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        if (userLocationRef.current) {
          map.removeLayer(userLocationRef.current);
        }

        const newMarker = L.marker([latitude, longitude], {
          icon: userLocationMarker,
        }).addTo(map);

        userLocationRef.current = newMarker;

        map.setView([latitude, longitude], 17);
        setLoading(false);
      }
    },
    [map, refetchMarker],
  );

  const fetchAndroidUserLocation = useCallback(() => {
    if (!isAndroid) return;
    if (!map) return;

    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.", {
        style: { maxWidth: 437, height: 50, borderRadius: 8 },
        autoClose: 2000,
        toastId: "browser_not_support_geolocation",
      });
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPerm("granted");

        const { latitude, longitude } = position.coords;

        if (!latitude || !longitude) {
          setLoading(false);
          return;
        }

        refetchMarker(latitude, longitude, 1);
        setUserLat(latitude);
        setUserLon(longitude);

        const userLocationMarker = L.divIcon({
          html: `
            <div class="relative flex justify-center items-center">
              <div class="relative z-0 inline-flex h-4 w-4 rounded-full bg-[#4593ee] border-2 border-white"></div>
              <div class="absolute inline-flex h-4 w-4 rounded-full bg-[#4593ee50] user-location animate-ping"></div>
              <div class="absolute inline-flex h-6 w-6 rounded-full bg-[#4593ee40] user-location animate-ping animate-delay-100"></div>
            </div>
          `,
          className: "",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        if (userLocationRef.current) {
          map.removeLayer(userLocationRef.current);
        }

        const newMarker = L.marker([latitude, longitude], {
          icon: userLocationMarker,
        }).addTo(map);

        userLocationRef.current = newMarker;

        // Center map to user's location
        map.setView([latitude, longitude], 17);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user's location:", error);
        toast.error(`Error fetching location: ${error.message}`, {
          style: { maxWidth: 437, height: 50, borderRadius: 8 },
          autoClose: 2000,
          toastId: "location_error",
        });
        setLoading(false);
        setPerm("error_fetching");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (!isMobile && desktopLocation?.latitude && desktopLocation?.longitude) {
      updateDesktopUserLocation(
        desktopLocation.latitude,
        desktopLocation.longitude,
      );
    }
  }, [isMobile, desktopLocation, updateDesktopUserLocation]);

  const [perm, setPerm] = useState<any>("checking");

  useEffect(() => {
    if (tele?.LocationManager) {
      tele.LocationManager.init();
      console.log("Telegram.WebApp.LocationManager initialized");
    } else {
      console.warn("Telegram.WebApp.LocationManager is not available.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIOSLocationPermission = useCallback(async () => {
    if (!isMobile) return;
    if (isAndroid) return;
    if (!tele?.LocationManager) {
      setPerm("prompt");
      return;
    }
    if (!tele.LocationManager.isLocationAvailable) {
      setPerm("denied");
      return;
    }
    // if (!tele.LocationManager.isAccessGranted) {
    //   setPerm("denied");
    //   return;
    // }
    setPerm("prompt");

    tele.LocationManager.getLocation((location: any) => {
      if (location) {
        setPerm("granted");
        setLoading(true);
        if (!map) return;
        const { latitude, longitude } = location;
        if (!latitude || !longitude) {
          setLoading(false);
          return;
        }
        setUserLat(latitude);
        setUserLon(longitude);

        refetchMarker(latitude, longitude, 1);
        const userLocationMarker = L.divIcon({
          html: `
            <div class="relative flex justify-center items-center">
              <div class="relative z-0 inline-flex h-4 w-4 rounded-full bg-[#4593ee] border-2 border-white"></div>
              <div class="absolute inline-flex h-4 w-4 rounded-full bg-[#4593ee50] user-location animate-ping"></div>
              <div class="absolute inline-flex h-6 w-6 rounded-full bg-[#4593ee40] user-location animate-ping animate-delay-100"></div>
            </div>
          `,
          className: "",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        if (userLocationRef.current) {
          map.removeLayer(userLocationRef.current);
        }

        const newMarker = L.marker([latitude, longitude], {
          icon: userLocationMarker,
        }).addTo(map);

        userLocationRef.current = newMarker;

        map.setView([latitude, longitude], 17);
        setLoading(false);
      } else {
        // toast.error("Failed to fetch location from Telegram.");
        setPerm("denied");
      }
    });
  }, [isMobile, tele.LocationManager, isAndroid, map, refetchMarker]);

  const watchIdRef = useRef<number | null>(null);
  const isRealTimeActive = useRef(false);

  const handleRealTimeLocation = useCallback(() => {
    if (!isMobile) return;

    if (!map || isRealTimeActive.current) return;

    if (isRealTimeActive.current) {
      console.warn("Real-time location tracking is already active.");
      return;
    }

    isRealTimeActive.current = true;

    if (!navigator.geolocation) {
      toast.error("Your browser does not support Geolocation API.", {
        style: { maxWidth: 437, height: 50, borderRadius: 8 },
        autoClose: 2000,
        toastId: "browser_not_support_geolocation",
      });
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      debounce((position) => {
        const { latitude, longitude } = position.coords;

        const userLocationMarker = L.divIcon({
          html: `
            <div class="relative flex justify-center items-center">
              <div class="relative z-0 inline-flex h-4 w-4 rounded-full bg-[#4593ee] border-2 border-white"></div>
              <div class="absolute inline-flex h-4 w-4 rounded-full bg-[#4593ee50] user-location animate-ping"></div>
              <div class="absolute inline-flex h-6 w-6 rounded-full bg-[#4593ee40] user-location animate-ping animate-delay-100"></div>
            </div>
          `,
          className: "",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        if (userLocationRef.current) {
          map.removeLayer(userLocationRef.current);
        }

        const newMarker = L.marker([latitude, longitude], {
          icon: userLocationMarker,
        }).addTo(map);

        userLocationRef.current = newMarker;

        // map.setView([latitude, longitude], 17);
      }, 1000),
      (error) => {
        console.error("Error fetching location:", error);
        if (error.code === error.PERMISSION_DENIED) {
          console.log("Error fetching user's location:", error.code);
        } else {
          toast.error(`Error fetching location: ${error.message}`, {
            style: { maxWidth: 437, height: 50, borderRadius: 8 },
            autoClose: 2000,
            toastId: "location_error",
          });
        }
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 15000,
      },
    );
  }, [isMobile, map]);

  const hasCheckedRealtime = useRef(false);
  useEffect(() => {
    if (perm === "granted" && map && !hasCheckedRealtime.current) {
      handleRealTimeLocation();
      hasCheckedRealtime.current = true;
    }
  }, [perm, handleRealTimeLocation, map]);

  const hasCheckedPermission = useRef(false);

  useEffect(() => {
    if (
      tele?.LocationManager?.isInited &&
      map &&
      !hasCheckedPermission.current
    ) {
      checkIOSLocationPermission();
      hasCheckedPermission.current = true;
    }
  }, [tele?.LocationManager?.isInited, checkIOSLocationPermission, map]);

  const hasCheckedLocationFirstTime = useRef(false);

  useEffect(() => {
    if (perm === "granted" && map && !hasCheckedPermission.current) {
      checkIOSLocationPermission();
      hasCheckedLocationFirstTime.current = true;
    }
  }, [checkIOSLocationPermission, perm, map]);

  useEffect(() => {
    fetchAndroidUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        console.log("Real-time location tracking stopped.");
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    // if (map) return;

    const mapInstance = L.map("map", {
      zoomControl: false,
      attributionControl: false,
      maxZoom: 22,
      minZoom: 13,
    }).setView([0, 0], 17);
    setMap(mapInstance);

    L.maplibreGL({
      style: "https://static.seeddao.org/openfreemap.json",
    }).addTo(mapInstance);

    return () => {
      mapInstance.remove();
    };
  }, []);

  // Initialize marker cluster group
  useEffect(() => {
    if (map && !markerClusterGroupRef.current) {
      const markerClusterGroup = L.markerClusterGroup();
      map.addLayer(markerClusterGroup);
      markerClusterGroupRef.current = markerClusterGroup;
    }

    return () => {
      if (map && markerClusterGroupRef.current) {
        map.removeLayer(markerClusterGroupRef.current);
        markerClusterGroupRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!locations || !markerClusterGroupRef.current) return;

    locations.forEach((location) => {
      const markerKey = `${location.latitude.toFixed(
        6,
      )},${location.longitude.toFixed(6)}`;
      if (existingMarkers.current.has(markerKey)) return;

      existingMarkers.current.add(markerKey);

      let iconType = markerOtherIconUrl;
      if (location?.metadata?.leisure) {
        iconType = markerParkIconUrl;
      }
      if (location?.metadata?.amenity) {
        iconType = markerSchoolIconUrl;
      }
      if (location?.metadata?.shop) {
        iconType = markerSupermarketIconUrl;
      }
      if (location?.metadata?.public_transport) {
        iconType = markerPublicTransportIconUrl;
      }
      const markerIcon = L.divIcon({
        html: `<div style="
          width: 60px; 
          height: 50px; 
          background-image: url(${iconType}); 
          background-size: contain; 
          background-repeat: no-repeat; 
          background-position: center;">
        </div>`,
        className: "custom-marker",
        iconSize: [50, 50],
      });

      const locationName =
        location?.name ||
        location?.metadata?.name ||
        (location?.metadata["name:en"] && location?.metadata["name:en"]);
      let marker;
      if (locationName) {
        marker = L.marker([location.latitude, location.longitude], {
          icon: markerIcon,
        }).bindPopup(`
          <div>
            <h3>${locationName}</h3>
          </div>
        `);
      } else {
        marker = L.marker([location.latitude, location.longitude], {
          icon: markerIcon,
        });
      }

      if (markerClusterGroupRef.current) {
        markerClusterGroupRef.current.addLayer(marker);
      }
    });
  }, [locations]);

  const [newLat, setNewLat] = useState<number | null>();
  const [newLon, setNewLon] = useState<number | null>();

  useEffect(() => {
    if (!map) return;

    const handleDragEnd = () => {
      if (!map || !userLat || !userLon) return;

      const center = map.getCenter();
      const newLat = center.lat;
      const newLon = center.lng;

      setNewLat(newLat);
      setNewLon(newLon);
      refetchMarker(newLat, newLon, 1);
    };

    map.on("dragend", handleDragEnd);

    return () => {
      map.off("dragend", handleDragEnd);
    };
  }, [map, refetchMarker, userLat, userLon]);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);

    toast.success("Copied to clipboard.", {
      style: { maxWidth: 437, height: 50, borderRadius: 8 },
      autoClose: 700,
      toastId: "copy_to_clipboard",
    });
  };

  const lat = (newLat ? newLat : userLat)?.toFixed(4);
  const lon = (newLon ? newLon : userLon)?.toFixed(4);

  const [caution, setCaution] = useState<boolean>(true);

  return (
    <div className="relative w-full h-screen">
      <ToastContainer
        position="top-left"
        closeOnClick
        transition={Slide}
        hideProgressBar
        limit={1}
        stacked
        className="top-3 max-w-[337px] left-[50%] -translate-x-[50%]"
      />
      <div id="map" className="absolute inset-0 w-full h-full z-0"></div>
      {/* <div className="fixed top-5 left-5 bg-black text-white w-[300px] break-words z-50">
        perm: {perm} {JSON.stringify(coords)}
      </div> */}
      {caution && !isMobile && (
        <div className="absolute z-10 mx-auto bottom-[120px] right-5 left-5 rounded-md text-center bg-black/70 backdrop-blur-sm text-white overflow-hidden">
          <div className="relative w-full h-full text-center px-6 py-4">
            Please use a mobile device to access the most accurate location
            services
            <div
              className="absolute -top-2 right-2 cursor-pointer text-[28px]"
              onClick={() => setCaution(false)}
            >
              ⊗
            </div>
          </div>
        </div>
      )}
      {(loading || loadingLocationByIP) && perm === "granted" && (
        <div className="fixed z-40 inset-0 bg-black/50 flex flex-col items-center justify-center font-medium gap-1">
          <span className="location-loader"></span>
          <div className="loading-anim">
            Checking your location
            <span className="dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        </div>
      )}
      {perm === "denied" && (
        <div className="fixed z-40 inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center font-medium gap-1">
          <span className="location-loader"></span>
          <div className="flex flex-col gap-2 text-white text-center items-center px-6 text-sm mt-1">
            <p className="px-3">
              You have not allowed location access. Please follow the
              instructions below
            </p>
            <div className="flex flex-col items-center gap-2 mt-1 text-sm">
              <div className="mt-1 flex flex-col gap-1">
                <span>
                  1. Access the{" "}
                  <span className="font-bold text-[#6ecc39]">
                    Seed bot (@seed_coin_bot)
                  </span>{" "}
                  and tap on{" "}
                  <span className="font-bold text-[#6ecc39]">Seed</span> to
                  access settings.
                </span>
                <img
                  alt=""
                  src="images/seed-go/guide-setting.png"
                  className="max-w-md w-full mx-auto object-contain rounded-md mt-2"
                />
              </div>

              <div className="mt-1 flex flex-col gap-1">
                <span>
                  2. Enable{" "}
                  <span className="font-bold text-[#6ecc39]">Geolocation</span>{" "}
                  and{" "}
                  <span className="font-bold text-[#6ecc39]">
                    manually restart
                  </span>{" "}
                  Seed bot.
                </span>
                <img
                  alt=""
                  src="images/seed-go/guide-enable-geo.png"
                  className="max-w-md w-full mx-auto object-contain rounded-md mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {perm === "error_fetching" && (
        <div className="fixed z-40 inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center font-medium gap-1">
          <span className="location-loader"></span>
          <div className="flex flex-col gap-3 text-white text-center items-center px-8 text-sm">
            <span>
              An error occurred. Please{" "}
              <span className="font-bold text-[#6ecc39]">restart</span> the app.
            </span>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-[5px] bg-[#4593ee] rounded cursor-pointer w-fit"
            >
              Reload
            </button>
          </div>
        </div>
      )}

      {perm === "prompt" && (
        <div className="fixed z-40 inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center font-medium gap-1">
          <span className="location-loader"></span>
          <div className="flex flex-col gap-3 text-white text-center items-center px-8 text-sm">
            <span>
              Please grant permission to access your location and{" "}
              <span className="font-bold text-[#6ecc39]">manually restart</span>{" "}
              the Seed bot.
            </span>
            <button
              onClick={() => {
                if (isAndroid) {
                  window.location.reload();
                  return;
                }
                checkIOSLocationPermission();
              }}
              className="px-5 py-[5px] bg-[#4593ee] rounded cursor-pointer w-fit"
            >
              Allow
            </button>
          </div>
        </div>
      )}
      {loadingLocation && (
        <div className="absolute z-10 top-5 left-5">
          <span className="map-loader shadow-lg"></span>
        </div>
      )}
      <button
        onClick={() => {
          isMobile && !isAndroid && checkIOSLocationPermission();
          isAndroid && fetchAndroidUserLocation();
          !isMobile &&
            updateDesktopUserLocation(
              desktopLocation.latitude!,
              desktopLocation.longitude!,
            );
        }}
        className="absolute z-10 bottom-4 left-4"
      >
        <LocationIc />
      </button>
      <button
        onClick={() => navigate(navPaths.MISSIONS)}
        className="absolute z-10 bottom-4 right-4"
      >
        <MissionIc />
      </button>
      <div className="absolute z-10 mx-auto bottom-4 right-0 left-0 w-fit h-fit cursor-pointer">
        <div className="relative">
          <InventoryIc />
          <img
            src="images/home/inventory.png"
            alt=""
            className="size-[50%] absolute inset-0 mx-auto my-auto"
          />
          <div className="absolute z-[-1] bg-white mx-auto my-auto inset-0 size-[60%] rounded-full animate-ping" />
        </div>
      </div>
      <div className="absolute top-[18px] right-4 shadow-md rounded-[60px] bg-white p-[2px] flex items-center gap-4">
        <div className="flex items-center gap-1 ml-2">
          <img
            alt=""
            src="images/seed-go/slove-coin.png"
            className="size-[24px] "
          />
          <p className="text-sm leading-[20.23px] font-bold mt-[2px]">0</p>
        </div>
        <div className="flex items-center gap-1">
          <img
            alt=""
            src="images/seed-go/seed-coin.png"
            className="size-[24px] "
          />
          <p className="text-sm leading-[20.23px] font-bold mt-[2px]">
            {/* {getNumberFormatUs(
              formatNumberFloatFix(Number(formatDecimals(balance)), 2),
            )} */}
            0
          </p>
        </div>
        <img
          alt=""
          src="images/seed-go/sui-token.png"
          className="size-[33px] "
        />
      </div>

      {lat && lon && (
        <div
          onClick={() => handleCopyToClipboard(`${lat},${lon}`)}
          className="absolute cursor-pointer top-[60px] right-4 text-xs flex gap-1 items-end pt-[6px] pb-1 px-2 rounded-md bg-white/50 backdrop-blur-sm text-gray-600 font-medium"
        >
          <div className="flex items-center justify-end gap-1 w-full">
            <span>{lat}</span>
          </div>
          -
          <div className="flex items-center justify-end gap-1 w-full">
            <span>{lon}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeedGo;
